import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
// Asegúrate de que esta ruta sea correcta
import { getDownloadXmlSatStatistics } from "../../actions/accounting/downloadXmlSatStatistics";

// 🛠️ FUNCIÓN AUXILIAR PARA DAR FORMATO A LA FECHA DE SOLICITUD
// Convierte una cadena ISO (ej: 2025-10-01T08:57:26.895193) a un formato legible
const formatSolicitudDate = (isoString) => {
  if (!isoString) return "";

  const date = new Date(isoString);

  // Usamos 'es-ES' para formato local y 'hour12: false' para 24 horas
  return date
    .toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", ""); // Elimina la coma si la localización la añade
};

export function StatisticsTable({ preReportId }) {
  const { binnacle } = useSelector((state) => state.downloadXmlSatStatistics);
  const dispatch = useDispatch();
  const [tooltip, setTooltip] = useState({
    visible: false,
    content: "",
    x: 0,
    y: 0,
  });
  const [tooltipContainer, setTooltipContainer] = useState(null);
  // Estado para ordenamiento
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [sortedData, setSortedData] = useState([]);
  const [titleTooltip, setTitleTooltip] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(true); // true para mostrar el efecto en la carga inicial

  useEffect(() => {
    if (preReportId != null && preReportId > 0) {
      setIsRefreshing(true); // activa el efecto al iniciar la carga
      dispatch(getDownloadXmlSatStatistics(preReportId));
    }
  }, [dispatch, preReportId]);

  useEffect(() => {
    setSortedData(binnacle || []);
  }, [binnacle]);

  useEffect(() => {
    if (isRefreshing && binnacle) {
      // Cuando los datos cambian, desactiva el refresco
      setIsRefreshing(false);
    }
  }, [binnacle]);

  // Ordena los datos cuando cambia la columna o dirección
  useEffect(() => {
    if (!sortConfig.key) return;
    const sorted = [...sortedData].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      // Si es fecha, convertir a Date
      if (sortConfig.key === "solicitud") {
        valA = new Date(valA);
        valB = new Date(valB);
      }
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    setSortedData(sorted);
  }, [sortConfig]);

  // Se mueve esta función aquí para que esté disponible en el cuerpo de la tabla
  // Usaremos row.estado en lugar de row.status
  const getStatusBadge = (status) => {
    if (status.includes("Finalizado OK")) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200">
          {status}
        </span>
      );
    } else if (status.includes("Para Procesar")) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
          {status}
        </span>
      );
    } else if (status.includes("Failed") || status.includes("Error")) {
      // Agregamos lógica para el estado de error (WebHook Recibido (Failed))
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-50 text-red-700 border border-red-200">
          {status}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
        {status}
      </span>
    );
  };

  // Eliminamos el array documentData ya que usaremos 'binnacle'

  // Prepare display data: if API returned no items after finishing fetch,
  // show 10 placeholder empty rows so the table keeps its shape.
  const placeholders = Array.from({ length: 10 }).map((_, i) => ({
    id: `placeholder-${i}`,
    proceso: "",
    movimiento: "",
    solicitud: null,
    estado: "Sin datos",
    metaProc: "",
    detalles: "",
  }));

  // displayData: use actual sortedData when present, otherwise placeholders
  // but while refreshing (initial load) keep an empty array so the overlay spinner shows
  const displayData =
    Array.isArray(sortedData) && sortedData.length > 0
      ? sortedData
      : isRefreshing
      ? []
      : placeholders;

  // Función para manejar el clic en el encabezado
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  return (
    <div className="container-fluid p-0" style={{ position: "relative" }}>
      <div className="row justify-content-center">
        <div className="col-12">
          {/* TÍTULO */}
          <div
            className="card-header bg-light text-center py-2"
            style={{ position: "relative" }}
          >
            <h5
              className="mb-0 font-weight-bold text-dark"
              style={{
                display: "inline-block",
                cursor: isRefreshing ? "wait" : "pointer",
                position: "relative",
                userSelect: "none",
              }}
              onMouseEnter={() => setTitleTooltip(true)}
              onMouseLeave={() => setTitleTooltip(false)}
              onClick={() => {
                if (preReportId != null && preReportId > 0 && !isRefreshing) {
                  setIsRefreshing(true);
                  dispatch(getDownloadXmlSatStatistics(preReportId));
                }
              }}
            >
              Estadística de descargas{"  "}
              <i
                className="fa fa-refresh"
                style={{
                  fontSize: 18,
                  verticalAlign: "middle",
                  marginLeft: 8,
                  color: "#9900fff0",
                  opacity: 0.9,
                  animation: isRefreshing
                    ? "fa-spin 1s infinite linear"
                    : undefined,
                }}
                aria-hidden="true"
              />
            </h5>
            {titleTooltip &&
              ReactDOM.createPortal(
                <div
                  style={{
                    position: "fixed",
                    left:
                      document
                        .querySelector(".card-header h5")
                        .getBoundingClientRect().left + 20,
                    top:
                      document
                        .querySelector(".card-header h5")
                        .getBoundingClientRect().bottom + 8,
                    background: "#9900fff0",
                    color: "#fff",
                    padding: "10px 18px",
                    borderRadius: "10px",
                    fontSize: "1rem",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
                    zIndex: 9999,
                    pointerEvents: "none",
                  }}
                >
                  De clic para actualizar la información
                  <span
                    style={{
                      position: "absolute",
                      left: "-16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 0,
                      height: 0,
                      borderTop: "8px solid transparent",
                      borderBottom: "8px solid transparent",
                      borderRight: "8px solid #333",
                    }}
                  />
                </div>,
                document.body
              )}
          </div>
          {/* TABLA CON SCROLL VERTICAL */}
          <div
            className="table-responsive"
            style={{
              maxHeight: "400px",
              overflowY: "auto",
              overflowX: "auto",
              position: "relative",
              pointerEvents: isRefreshing ? "none" : "auto",
              opacity: isRefreshing ? 0.5 : 1,
            }}
            ref={setTooltipContainer}
          >
            {/* Overlay con spinner */}
            {isRefreshing && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: "rgba(255,255,255,0.5)",
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="fa fa-refresh fa-spin"
                  style={{ fontSize: 40, color: "#9900fff0" }}
                  aria-hidden="true"
                />
              </div>
            )}
            <table
              className="table table-bordered table-sm mb-0"
              style={{ tableLayout: "auto", minWidth: "900px" }}
            >
              <thead className="thead-light">
                <tr>
                  <th
                    className="text-center align-middle"
                    style={{
                      minWidth: "120px",
                      maxWidth: "400px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      background: "#f8f9fa",
                      zIndex: 2,
                    }}
                    onClick={() => handleSort("proceso")}
                  >
                    Proceso{" "}
                    {sortConfig.key === "proceso"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    className="text-center align-middle"
                    style={{
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      background: "#f8f9fa",
                      zIndex: 2,
                    }}
                    onClick={() => handleSort("movimiento")}
                  >
                    Movimiento{" "}
                    {sortConfig.key === "movimiento"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    className="text-center align-middle"
                    style={{
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      background: "#f8f9fa",
                      zIndex: 2,
                    }}
                    onClick={() => handleSort("solicitud")}
                  >
                    Solicitud{" "}
                    {sortConfig.key === "solicitud"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    className="text-center align-middle"
                    style={{
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      background: "#f8f9fa",
                      zIndex: 2,
                    }}
                    onClick={() => handleSort("metaProc")}
                  >
                    Meta - Proc{" "}
                    {sortConfig.key === "metaProc"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    className="text-left align-middle"
                    style={{
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      background: "#f8f9fa",
                      zIndex: 2,
                    }}
                    onClick={() => handleSort("estado")}
                  >
                    Estado{" "}
                    {sortConfig.key === "estado"
                      ? sortConfig.direction === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row) => (
                  <tr
                    key={row.id}
                    style={{ height: "1px", verticalAlign: "middle" }}
                    onMouseEnter={(e) => {
                      // Buscamos la celda de detalles en el renglón
                      const detallesCell =
                        e.currentTarget.querySelector("td:last-child");
                      if (detallesCell) {
                        const cellRect = detallesCell.getBoundingClientRect();
                        setTooltip({
                          visible: true,
                          content: row.detalles,
                          x: cellRect.right + 24,
                          y: cellRect.top + cellRect.height / 2,
                        });
                      }
                    }}
                    onMouseLeave={() =>
                      setTooltip({ ...tooltip, visible: false })
                    }
                  >
                    <td
                      className="font-weight-bold align-middle"
                      style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}
                    >
                      {row.proceso}
                    </td>
                    <td
                      className="align-middle text-center"
                      style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}
                    >
                      {row.movimiento}
                    </td>
                    <td
                      className="text-center align-middle"
                      style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}
                    >
                      {formatSolicitudDate(row.solicitud)}
                    </td>
                    <td
                      className="text-center align-middle"
                      style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}
                    >
                      <span className="font-weight-bold text-dark">
                        {row.metaProc}
                      </span>
                    </td>
                    <td
                      className="text-left align-middle"
                      style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}
                    >
                      {getStatusBadge(row.estado)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Tooltip tipo globo de conversación */}
            {tooltip.visible &&
              ReactDOM.createPortal(
                <div
                  className="custom-tooltip"
                  style={{
                    position: "fixed",
                    left: tooltip.x,
                    top: tooltip.y,
                    transform: "translateY(-50%)",
                    background: "#3b831fff",
                    color: "#fff",
                    padding: "18px 32px",
                    borderRadius: "14px",
                    fontSize: "1.15rem",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.22)",
                    maxWidth: "600px",
                    whiteSpace: "pre-line",
                    zIndex: 9999,
                    pointerEvents: "none",
                  }}
                >
                  {tooltip.content}
                  <span
                    style={{
                      position: "absolute",
                      left: "-24px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 0,
                      height: 0,
                      borderTop: "12px solid transparent",
                      borderBottom: "12px solid transparent",
                      borderRight: "12px solid #333",
                    }}
                  />
                </div>,
                document.body
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
